import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Shield, Users, FileText, Clock, Ban, Check, X, Globe, Image, Trash2, Power, Plus, Eye, EyeOff, ArrowLeft, Pencil, RefreshCw, Sparkles, Settings, DollarSign, Key, Copy, Terminal } from 'lucide-react';
import { toast } from 'sonner';
import PromptGenerator from '@/components/PromptGenerator';
import LLMPricingAdmin from '@/components/admin/LLMPricingAdmin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLORS = {
  platinum: 'bg-slate-200 text-slate-800 border-slate-300',
  gold: 'bg-amber-100 text-amber-800 border-amber-300',
  silver: 'bg-gray-100 text-gray-600 border-gray-300',
};
const TIER_LABELS = { platinum: 'Platinum (60%)', gold: 'Gold (30%)', silver: 'Silver (10%)' };

export default function AdminDashboard() {
  const { user, token, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingPlatforms, setPendingPlatforms] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  // Banner create dialog
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [bnName, setBnName] = useState('');
  const [bnUrl, setBnUrl] = useState('');
  const [bnTier, setBnTier] = useState('silver');
  const [bnImage, setBnImage] = useState('');
  const [bnSaving, setBnSaving] = useState(false);

  // Banner preview
  const [previewBanner, setPreviewBanner] = useState(null);

  // Platform management
  const [allPlatforms, setAllPlatforms] = useState([]);
  const [platDialogOpen, setPlatDialogOpen] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [pName, setPName] = useState('');
  const [pDesc, setPDesc] = useState('');
  const [pIcon, setPIcon] = useState('');
  const [pWebsite, setPWebsite] = useState('');
  const [pSaving, setPSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  // Settings state
  const [karmaThreshold, setKarmaThreshold] = useState(5);
  const [karmaThresholdInput, setKarmaThresholdInput] = useState('5');
  const [settingsSaving, setSettingsSaving] = useState(false);

  // Admin API token state
  const [adminApiToken, setAdminApiToken] = useState(null);
  const [showAdminToken, setShowAdminToken] = useState(false);
  const [adminTokenLoading, setAdminTokenLoading] = useState(false);

  useEffect(() => {
    // Wait for auth to load before checking admin status
    if (authLoading) return;
    if (!user || !isAdmin) { navigate('/'); return; }
    const fetchData = async () => {
      try {
        const [statsRes, usersRes, pendingRes, bannersRes, allPlatsRes, settingsRes, apiTokenRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers }),
          axios.get(`${API}/admin/users`, { headers }),
          axios.get(`${API}/admin/platforms/pending`, { headers }),
          axios.get(`${API}/admin/banners`, { headers }),
          axios.get(`${API}/admin/platforms`, { headers }),
          axios.get(`${API}/settings`),
          axios.get(`${API}/user/api-token`, { headers }),
        ]);
        setStats(statsRes.data);
        setUsers(usersRes.data.users);
        setPendingPlatforms(pendingRes.data);
        setBanners(bannersRes.data);
        setAllPlatforms(allPlatsRes.data);
        const thresh = settingsRes.data.karma_filter_threshold ?? 5;
        setKarmaThreshold(thresh);
        setKarmaThresholdInput(String(thresh));
        setAdminApiToken(apiTokenRes.data.api_token);
      } catch {
        toast.error('Failed to load admin data');
      }
      setLoading(false);
    };
    fetchData();
  }, [user, isAdmin, navigate, token, authLoading]);

  const handleBlockUser = async (userId) => {
    try {
      const res = await axios.put(`${API}/admin/users/${userId}/block`, {}, { headers });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_blocked: res.data.is_blocked } : u));
      toast.success(res.data.is_blocked ? 'User blocked' : 'User unblocked');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Permanently delete user "${username}" and all their data? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('User deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleApprovePlatform = async (platformId) => {
    try {
      await axios.put(`${API}/platforms/${platformId}/approve`, {}, { headers });
      setPendingPlatforms(prev => prev.filter(p => p.id !== platformId));
      setStats(prev => prev ? { ...prev, pending_platforms: prev.pending_platforms - 1 } : prev);
      toast.success('Platform approved');
    } catch { toast.error('Failed to approve'); }
  };

  const handleRejectPlatform = async (platformId) => {
    try {
      await axios.put(`${API}/platforms/${platformId}/reject`, {}, { headers });
      setPendingPlatforms(prev => prev.filter(p => p.id !== platformId));
      setStats(prev => prev ? { ...prev, pending_platforms: prev.pending_platforms - 1 } : prev);
      toast.success('Platform rejected');
    } catch { toast.error('Failed to reject'); }
  };

  const openPlatformDialog = (platform = null) => {
    if (platform) {
      setEditingPlatform(platform);
      setPName(platform.name);
      setPDesc(platform.description || '');
      setPIcon(platform.icon || '');
      setPWebsite(platform.website || '');
    } else {
      setEditingPlatform(null);
      setPName(''); setPDesc(''); setPIcon(''); setPWebsite('');
    }
    setPlatDialogOpen(true);
  };

  const handleSavePlatform = async (e) => {
    e.preventDefault();
    if (!pName.trim()) { toast.error('Name is required'); return; }
    setPSaving(true);
    try {
      if (editingPlatform) {
        const res = await axios.put(`${API}/admin/platforms/${editingPlatform.id}`, { name: pName, description: pDesc, icon: pIcon, website: pWebsite }, { headers });
        setAllPlatforms(prev => prev.map(p => p.id === editingPlatform.id ? res.data : p));
        toast.success('Platform updated');
      } else {
        const res = await axios.post(`${API}/admin/platforms`, { name: pName, description: pDesc, icon: pIcon, website: pWebsite }, { headers });
        setAllPlatforms(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success('Platform created');
      }
      setPlatDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save platform');
    }
    setPSaving(false);
  };

  const handleDeletePlatform = async (platformId) => {
    if (!window.confirm('Delete this platform? Prompts using it will keep their platform reference.')) return;
    try {
      await axios.delete(`${API}/admin/platforms/${platformId}`, { headers });
      setAllPlatforms(prev => prev.filter(p => p.id !== platformId));
      toast.success('Platform deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const [crawlingId, setCrawlingId] = useState(null);
  const handleCrawlPlatform = async (platformId) => {
    setCrawlingId(platformId);
    try {
      const res = await axios.post(`${API}/admin/platforms/${platformId}/crawl`, {}, { headers });
      setAllPlatforms(prev => prev.map(p => p.id === platformId ? res.data : p));
      toast.success('Platform crawled successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Crawl failed');
    }
    setCrawlingId(null);
  };

  // Banner handlers
  const handleBannerImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be under 2MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setBnImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleCreateBanner = async (e) => {
    e.preventDefault();
    if (!bnImage) { toast.error('Upload a banner image'); return; }
    setBnSaving(true);
    try {
      const res = await axios.post(`${API}/admin/banners`, {
        name: bnName || 'Untitled Banner',
        image: bnImage,
        click_url: bnUrl,
        tier: bnTier,
      }, { headers });
      setBanners(prev => [res.data, ...prev]);
      setBannerDialogOpen(false);
      setBnName(''); setBnUrl(''); setBnTier('silver'); setBnImage('');
      toast.success('Banner created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create banner');
    }
    setBnSaving(false);
  };

  const handleToggleBanner = async (bannerId, currentEnabled) => {
    try {
      const res = await axios.put(`${API}/admin/banners/${bannerId}`, { enabled: !currentEnabled }, { headers });
      setBanners(prev => prev.map(b => b.id === bannerId ? res.data : b));
      toast.success(!currentEnabled ? 'Banner enabled' : 'Banner disabled');
    } catch { toast.error('Failed to update'); }
  };

  const handleUpdateBannerTier = async (bannerId, tier) => {
    try {
      const res = await axios.put(`${API}/admin/banners/${bannerId}`, { tier }, { headers });
      setBanners(prev => prev.map(b => b.id === bannerId ? res.data : b));
      toast.success('Tier updated');
    } catch { toast.error('Failed to update'); }
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await axios.delete(`${API}/admin/banners/${bannerId}`, { headers });
      setBanners(prev => prev.filter(b => b.id !== bannerId));
      toast.success('Banner deleted');
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 py-12 animate-pulse">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 rounded-xl bg-muted" />)}
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="admin-dashboard">
      <div className="flex items-center gap-3 mb-8">
        <Shield className="w-7 h-7 text-primary" />
        <h1 className="text-3xl font-bold tracking-tight">Moderation Dashboard</h1>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          {[
            { label: 'Total Users', value: stats.users, icon: Users, color: 'text-blue-500' },
            { label: 'Total Prompts', value: stats.prompts, icon: FileText, color: 'text-emerald-500' },
            { label: 'Pending Platforms', value: stats.pending_platforms, icon: Clock, color: 'text-amber-500' },
            { label: 'Blocked Users', value: stats.blocked_users, icon: Ban, color: 'text-red-500' },
          ].map(stat => (
            <Card key={stat.label} className="p-4 border-border/50">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg bg-muted flex items-center justify-center ${stat.color}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Tabs defaultValue="banners" className="space-y-4">
        <TabsList className="rounded-full">
          <TabsTrigger value="banners" className="rounded-full gap-1.5" data-testid="tab-banners">
            <Image className="w-3.5 h-3.5" /> Banners
            <Badge className="ml-1 bg-primary text-primary-foreground h-5 px-1.5 text-xs">{banners.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="platforms" className="rounded-full gap-1.5" data-testid="tab-platforms">
            <Clock className="w-3.5 h-3.5" /> Pending Platforms
            {pendingPlatforms.length > 0 && (
              <Badge className="ml-1 bg-amber-500 text-white h-5 px-1.5 text-xs">{pendingPlatforms.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="users" className="rounded-full gap-1.5" data-testid="tab-users">
            <Users className="w-3.5 h-3.5" /> Manage Users
          </TabsTrigger>
          <TabsTrigger value="generator" className="rounded-full gap-1.5" data-testid="tab-generator">
            <Sparkles className="w-3.5 h-3.5" /> Prompt Generator
          </TabsTrigger>
          <TabsTrigger value="settings" className="rounded-full gap-1.5" data-testid="tab-settings">
            <Settings className="w-3.5 h-3.5" /> Settings
          </TabsTrigger>
          <TabsTrigger value="llm-pricing" className="rounded-full gap-1.5" data-testid="tab-llm-pricing">
            <DollarSign className="w-3.5 h-3.5" /> LLM Pricing
          </TabsTrigger>
        </TabsList>

        {/* Banners Tab */}
        <TabsContent value="banners" data-testid="banners-tab">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              Manage ad banners shown on prompt detail pages. Platinum: 60%, Gold: 30%, Silver: 10% frequency.
            </p>
            <Button className="rounded-full gap-1.5 shadow-lg shadow-primary/20" onClick={() => setBannerDialogOpen(true)} data-testid="create-banner-btn">
              <Plus className="w-4 h-4" /> Create Banner
            </Button>
          </div>

          {banners.length === 0 ? (
            <Card className="p-8 text-center border-border/50">
              <Image className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">No banners yet. Create your first banner ad.</p>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="banners-list">
              {banners.map(b => (
                <Card key={b.id} className={`p-4 border-border/50 ${!b.enabled ? 'opacity-60' : ''}`} data-testid={`banner-item-${b.id}`}>
                  <div className="flex items-center gap-4">
                    {/* Preview */}
                    <div className="shrink-0 rounded-md overflow-hidden border border-border bg-muted" style={{ width: 180, height: 17 }}>
                      <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm truncate">{b.name}</span>
                        <Badge className={`rounded-full text-xs ${TIER_COLORS[b.tier] || TIER_COLORS.silver}`}>
                          {b.tier?.charAt(0).toUpperCase() + b.tier?.slice(1)}
                        </Badge>
                        {b.enabled ? (
                          <Badge className="rounded-full text-xs bg-emerald-500/10 text-emerald-600 border-emerald-500/20">Active</Badge>
                        ) : (
                          <Badge variant="outline" className="rounded-full text-xs text-muted-foreground">Disabled</Badge>
                        )}
                      </div>
                      {b.click_url && (
                        <p className="text-xs text-muted-foreground truncate">{b.click_url}</p>
                      )}
                      {/* Stats row */}
                      <div className="flex items-center gap-4 mt-1.5">
                        <span className="text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">{(b.impressions || 0).toLocaleString()}</span> impressions
                        </span>
                        <span className="text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">{(b.clicks || 0).toLocaleString()}</span> clicks
                        </span>
                        <span className={`text-xs font-semibold ${(b.ctr || 0) > 0 ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                          {(b.ctr || 0).toFixed(2)}% CTR
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full h-8 gap-1 text-xs"
                        onClick={() => setPreviewBanner(b)}
                        data-testid={`banner-preview-${b.id}`}
                      >
                        <Eye className="w-3 h-3" /> Preview
                      </Button>
                      <Select value={b.tier} onValueChange={(v) => handleUpdateBannerTier(b.id, v)}>
                        <SelectTrigger className="h-8 w-[130px] text-xs rounded-full" data-testid={`banner-tier-select-${b.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="platinum">Platinum (60%)</SelectItem>
                          <SelectItem value="gold">Gold (30%)</SelectItem>
                          <SelectItem value="silver">Silver (10%)</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full h-8 gap-1 text-xs"
                        onClick={() => handleToggleBanner(b.id, b.enabled)}
                        data-testid={`banner-toggle-${b.id}`}
                      >
                        {b.enabled ? <><EyeOff className="w-3 h-3" /> Disable</> : <><Eye className="w-3 h-3" /> Enable</>}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                        onClick={() => handleDeleteBanner(b.id)}
                        data-testid={`banner-delete-${b.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Create Banner Dialog */}
          <Dialog open={bannerDialogOpen} onOpenChange={setBannerDialogOpen}>
            <DialogContent className="max-w-md" data-testid="create-banner-dialog">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold">Create Banner</DialogTitle>
                <p className="text-sm text-muted-foreground">Upload a banner ad (540 x 50px recommended).</p>
              </DialogHeader>
              <form onSubmit={handleCreateBanner} className="space-y-4 mt-2" data-testid="create-banner-form">
                <div className="space-y-2">
                  <Label>Banner Name</Label>
                  <Input value={bnName} onChange={(e) => setBnName(e.target.value)} placeholder="e.g. Spring Sale Banner" className="h-10" data-testid="banner-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Banner Image</Label>
                  {bnImage ? (
                    <div className="relative rounded-lg overflow-hidden border border-border bg-muted">
                      <img src={bnImage} alt="Preview" style={{ width: '100%', height: 'auto' }} />
                      <button type="button" onClick={() => setBnImage('')} className="absolute top-1 right-1 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center">
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center h-16 rounded-lg border-2 border-dashed border-border hover:border-primary/50 cursor-pointer transition-colors" data-testid="banner-image-upload">
                      <span className="text-sm text-muted-foreground">Click to upload (540x50px, max 2MB)</span>
                      <input type="file" accept="image/*" className="hidden" onChange={handleBannerImageUpload} />
                    </label>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Click URL</Label>
                  <Input value={bnUrl} onChange={(e) => setBnUrl(e.target.value)} placeholder="https://advertiser.com" className="h-10" data-testid="banner-url-input" />
                </div>
                <div className="space-y-2">
                  <Label>Priority Tier</Label>
                  <Select value={bnTier} onValueChange={setBnTier}>
                    <SelectTrigger className="h-10" data-testid="banner-tier-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="platinum">Platinum - 60% frequency</SelectItem>
                      <SelectItem value="gold">Gold - 30% frequency</SelectItem>
                      <SelectItem value="silver">Silver - 10% frequency</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full h-10 rounded-full shadow-lg shadow-primary/20" disabled={bnSaving} data-testid="banner-submit-btn">
                  {bnSaving ? 'Creating...' : 'Create Banner'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Banner Preview Dialog */}
          <Dialog open={!!previewBanner} onOpenChange={(open) => !open && setPreviewBanner(null)}>
            <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="banner-preview-dialog">
              <div className="bg-background p-6 sm:p-8">
                <DialogHeader className="mb-4">
                  <DialogTitle className="text-lg font-bold flex items-center gap-2">
                    <Eye className="w-4 h-4" /> Banner Preview — Prompt Detail Page
                  </DialogTitle>
                </DialogHeader>

                {/* Simulated prompt detail page */}
                <div className="max-w-3xl mx-auto rounded-xl border border-border/50 bg-card overflow-hidden">
                  <div className="px-4 sm:px-6 py-6">
                    {/* Fake back button */}
                    <div className="flex items-center gap-2 mb-5 text-muted-foreground/50 text-sm">
                      <ArrowLeft className="w-4 h-4" /> Back
                    </div>

                    {/* Banner — exactly as it renders on PromptDetail */}
                    {previewBanner && previewBanner.image && (
                      <div className="mb-4 flex justify-center" data-testid="preview-ad-banner">
                        {previewBanner.click_url ? (
                          <a href={previewBanner.click_url} target="_blank" rel="noopener noreferrer" className="block rounded-lg overflow-hidden hover:opacity-90 transition-opacity">
                            <img src={previewBanner.image} alt={previewBanner.name || 'Ad'} style={{ width: 540, height: 'auto' }} className="rounded-lg" />
                          </a>
                        ) : (
                          <img src={previewBanner.image} alt={previewBanner.name || 'Ad'} style={{ width: 540, height: 'auto' }} className="rounded-lg" />
                        )}
                      </div>
                    )}

                    {/* Fake prompt card */}
                    <div className="rounded-lg border border-border/50 p-5">
                      <div className="flex gap-2 mb-3">
                        <span className="text-xs px-2.5 py-0.5 rounded-full bg-primary/10 text-primary">Marketing</span>
                        <span className="text-xs px-2.5 py-0.5 rounded-full border border-border text-muted-foreground">ChatGPT</span>
                      </div>
                      <div className="h-5 w-3/4 bg-muted rounded mb-2" />
                      <div className="flex gap-3 mb-4 text-xs text-muted-foreground/50">
                        <span>MASTERCONTROL</span>
                        <span>Jan 15, 2026</span>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-4 border border-border/30 space-y-2">
                        <div className="h-3 w-full bg-muted rounded" />
                        <div className="h-3 w-5/6 bg-muted rounded" />
                        <div className="h-3 w-4/6 bg-muted rounded" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Banner info */}
                {previewBanner && (
                  <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
                    <Badge className={`rounded-full text-xs ${TIER_COLORS[previewBanner.tier] || TIER_COLORS.silver}`}>
                      {previewBanner.tier?.charAt(0).toUpperCase() + previewBanner.tier?.slice(1)}
                    </Badge>
                    <span>{previewBanner.name}</span>
                    {previewBanner.click_url && <span className="truncate max-w-[200px]">{previewBanner.click_url}</span>}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Platforms Tab */}
        <TabsContent value="platforms" data-testid="platforms-tab">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">Manage platforms available for prompt categorization</p>
            <Button size="sm" className="rounded-full gap-1.5" onClick={() => openPlatformDialog()} data-testid="create-platform-btn">
              <Plus className="w-3.5 h-3.5" /> Create Platform
            </Button>
          </div>

          {/* Pending Platforms */}
          {pendingPlatforms.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-amber-600 mb-2">Pending Approval ({pendingPlatforms.length})</h3>
              <div className="space-y-2">
                {pendingPlatforms.map(p => (
                  <Card key={p.id} className="p-4 border-amber-500/30" data-testid={`pending-platform-${p.id}`}>
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <span className="font-semibold">{p.name}</span>
                        {p.description && <span className="text-sm text-muted-foreground ml-2">{p.description}</span>}
                        {p.submitted_by_username && <span className="text-xs text-muted-foreground ml-2">by {p.submitted_by_username}</span>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button size="sm" className="rounded-full gap-1 h-7 text-xs bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => handleApprovePlatform(p.id)} data-testid={`approve-platform-${p.id}`}>
                          <Check className="w-3 h-3" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full gap-1 h-7 text-xs text-destructive hover:bg-destructive/10" onClick={() => handleRejectPlatform(p.id)} data-testid={`reject-platform-${p.id}`}>
                          <X className="w-3 h-3" /> Reject
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* All Platforms */}
          <div className="space-y-2">
            {allPlatforms.map(p => (
              <Card key={p.id} className={`p-4 border-border/50 ${p.status !== 'approved' ? 'opacity-60' : ''}`} data-testid={`platform-${p.id}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{p.name}</span>
                      <Badge variant="outline" className="rounded-full text-[10px] h-5">{p.status}</Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                      {p.description && <span>{p.description}</span>}
                      {p.website && <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{p.website}</span>}
                      {p.last_crawled_at && <span className="text-[10px]">Crawled: {new Date(p.last_crawled_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleCrawlPlatform(p.id)} disabled={crawlingId === p.id} title="Re-crawl platform" data-testid={`crawl-platform-${p.id}`}>
                      <RefreshCw className={`w-3.5 h-3.5 ${crawlingId === p.id ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openPlatformDialog(p)} data-testid={`edit-platform-${p.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10" onClick={() => handleDeletePlatform(p.id)} data-testid={`delete-platform-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Create/Edit Platform Dialog */}
          <Dialog open={platDialogOpen} onOpenChange={setPlatDialogOpen}>
            <DialogContent data-testid="platform-dialog">
              <DialogHeader>
                <DialogTitle>{editingPlatform ? 'Edit Platform' : 'Create Platform'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSavePlatform} className="space-y-4 mt-2">
                <div>
                  <Label htmlFor="pname">Name *</Label>
                  <Input id="pname" value={pName} onChange={(e) => setPName(e.target.value)} required className="h-10" data-testid="platform-name-input" />
                </div>
                <div>
                  <Label htmlFor="pdesc">Description</Label>
                  <Input id="pdesc" value={pDesc} onChange={(e) => setPDesc(e.target.value)} className="h-10" data-testid="platform-desc-input" />
                </div>
                <div>
                  <Label htmlFor="picon">Icon</Label>
                  <Input id="picon" value={pIcon} onChange={(e) => setPIcon(e.target.value)} placeholder="e.g. message-square" className="h-10" data-testid="platform-icon-input" />
                </div>
                <div>
                  <Label htmlFor="pwebsite">Website</Label>
                  <Input id="pwebsite" value={pWebsite} onChange={(e) => setPWebsite(e.target.value)} placeholder="https://..." className="h-10" data-testid="platform-website-input" />
                </div>
                <Button type="submit" className="w-full h-10 rounded-full gap-2" disabled={pSaving} data-testid="platform-save-btn">
                  {pSaving ? 'Saving...' : editingPlatform ? 'Update Platform' : 'Create Platform'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" data-testid="users-tab">
          <Card className="border-border/50 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">
                      <Link to={`/profile/${u.id}`} className="hover:text-primary hover:underline transition-colors" data-testid={`user-profile-link-${u.id}`}>
                        {u.username}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">{u.email}</TableCell>
                    <TableCell>
                      <Badge variant={u.role === 'admin' ? 'default' : 'outline'} className="rounded-full text-xs">{u.role}</Badge>
                    </TableCell>
                    <TableCell>
                      {u.is_blocked ? (
                        <Badge variant="destructive" className="rounded-full text-xs gap-1"><Ban className="w-3 h-3" /> Blocked</Badge>
                      ) : (
                        <Badge className="rounded-full text-xs bg-emerald-500/10 text-emerald-600 border-emerald-500/20">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      {u.role !== 'admin' && (
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            size="sm"
                            variant={u.is_blocked ? 'outline' : 'destructive'}
                            className="rounded-full text-xs h-7 gap-1"
                            onClick={() => handleBlockUser(u.id)}
                            data-testid={`block-user-${u.id}`}
                          >
                            {u.is_blocked ? <><Check className="w-3 h-3" /> Unblock</> : <><Ban className="w-3 h-3" /> Block</>}
                          </Button>
                          {u.is_blocked && (
                            <Button
                              size="sm"
                              variant="destructive"
                              className="rounded-full text-xs h-7 gap-1"
                              onClick={() => handleDeleteUser(u.id, u.username)}
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-3 h-3" /> Delete
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Prompt Generator Tab */}
        <TabsContent value="generator" data-testid="generator-tab">
          <PromptGenerator token={token} />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" data-testid="settings-tab">
          <div className="max-w-xl space-y-6">
            {/* Admin API Token */}
            <div>
              <h3 className="font-semibold text-sm mb-1 flex items-center gap-1.5"><Key className="w-4 h-4 text-primary" /> Admin API Token</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Use this token to create users and manage prompts via the API. Keep it secret.
              </p>
            </div>
            <Card className="p-5 border-border/50 space-y-4" data-testid="admin-api-token-card">
              {adminApiToken ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 font-mono text-sm bg-muted/50 border border-border rounded-lg px-4 py-2.5 truncate" data-testid="admin-token-display">
                      {showAdminToken ? adminApiToken : '\u2022'.repeat(32)}
                    </div>
                    <Button variant="ghost" size="icon" className="shrink-0" onClick={() => setShowAdminToken(!showAdminToken)} data-testid="admin-toggle-token">
                      {showAdminToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="icon" className="shrink-0" onClick={() => { navigator.clipboard.writeText(adminApiToken); toast.success('Admin API token copied'); }} data-testid="admin-copy-token">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" className="rounded-full gap-1.5 text-xs" disabled={adminTokenLoading} data-testid="admin-regenerate-token"
                      onClick={async () => {
                        setAdminTokenLoading(true);
                        try {
                          const res = await axios.post(`${API}/user/api-token`, {}, { headers });
                          setAdminApiToken(res.data.api_token);
                          setShowAdminToken(true);
                          toast.success('Admin API token regenerated');
                        } catch { toast.error('Failed to regenerate'); }
                        setAdminTokenLoading(false);
                      }}>
                      <RefreshCw className={`w-3 h-3 ${adminTokenLoading ? 'animate-spin' : ''}`} /> Regenerate
                    </Button>
                  </div>
                  <div className="bg-muted/30 border border-border rounded-lg p-3 space-y-2">
                    <p className="text-xs font-semibold flex items-center gap-1"><Terminal className="w-3 h-3" /> Create User via API</p>
                    <pre className="text-xs bg-zinc-900 text-zinc-100 rounded-lg px-3 py-2 overflow-x-auto font-mono whitespace-pre">{`curl -X POST https://promptvault.org/api/v1/users \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${showAdminToken ? adminApiToken : '<admin_token>'}" \\
  -d '{"username":"newuser","email":"u@e.com","password":"pass123"}'`}</pre>
                  </div>
                </>
              ) : (
                <div className="text-center py-3">
                  <Button className="rounded-full gap-2" disabled={adminTokenLoading} data-testid="admin-generate-token"
                    onClick={async () => {
                      setAdminTokenLoading(true);
                      try {
                        const res = await axios.post(`${API}/user/api-token`, {}, { headers });
                        setAdminApiToken(res.data.api_token);
                        setShowAdminToken(true);
                        toast.success('Admin API token generated');
                      } catch { toast.error('Failed to generate'); }
                      setAdminTokenLoading(false);
                    }}>
                    <Key className="w-4 h-4" /> Generate Admin API Token
                  </Button>
                </div>
              )}
            </Card>

            {/* Karma Settings */}
            <div>
              <h3 className="font-semibold text-sm mb-1">User Filter Settings</h3>
              <p className="text-xs text-muted-foreground">
                Controls which users appear in the "User" filter on the public homepage.
                Only users whose Karma score meets or exceeds this threshold will be shown.
              </p>
            </div>
            <Card className="p-5 border-border/50 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Minimum Karma to appear in User filter</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    min={0}
                    value={karmaThresholdInput}
                    onChange={e => setKarmaThresholdInput(e.target.value)}
                    className="rounded-full h-9 w-32 text-sm text-center"
                  />
                  <span className="text-xs text-muted-foreground">Current: ⚡{karmaThreshold}</span>
                </div>
                <p className="text-xs text-muted-foreground">Default is 5. Set to 0 to show all users.</p>
              </div>
              <Button
                className="rounded-full gap-2"
                disabled={settingsSaving}
                onClick={async () => {
                  const val = parseInt(karmaThresholdInput, 10);
                  if (isNaN(val) || val < 0) return toast.error('Please enter a valid number ≥ 0');
                  setSettingsSaving(true);
                  try {
                    await axios.put(`${API}/admin/settings`, { karma_filter_threshold: val }, { headers });
                    setKarmaThreshold(val);
                    toast.success(`Karma threshold updated to ${val}`);
                  } catch {
                    toast.error('Failed to save settings');
                  }
                  setSettingsSaving(false);
                }}
              >
                {settingsSaving ? <><RefreshCw className="w-4 h-4 animate-spin" /> Saving…</> : 'Save Settings'}
              </Button>
            </Card>
          </div>
        </TabsContent>

        {/* LLM Pricing Tab */}
        <TabsContent value="llm-pricing" data-testid="llm-pricing-tab">
          <LLMPricingAdmin token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
